import {
	ArrowLeft,
	ExternalLink,
	Eye,
	EyeOff,
	GitMerge,
	Package,
	Save,
	Store,
	Trash2,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchJson, sendJson } from "../api/rest";
import {
	type BrandDetail,
	type BrandOverridePatch,
	brandPageUrl,
	INDEXABLE_COMPARABLE,
} from "../types/brands";

// ---------------------------------------------------------------------------
// Brand detail + curation
//
// Everything here is keyed on the slug. `brands.id` is regenerated by every nightly rebuild, and
// curation is stored in `brand_overrides` rather than on the brand row for the same reason — the
// rollup is a DELETE + INSERT and would wipe anything typed onto it.
// ---------------------------------------------------------------------------

const Section = ({
	title,
	subtitle,
	children,
}: {
	title: string;
	subtitle?: string;
	children: React.ReactNode;
}) => (
	<section className="bg-white border border-slate-200 rounded-xl p-5">
		<h2 className="text-sm font-semibold text-slate-700">{title}</h2>
		{subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
		<div className="mt-4">{children}</div>
	</section>
);

const Brand = () => {
	const { slug = "" } = useParams();
	const [brand, setBrand] = useState<BrandDetail | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState(false);

	const [form, setForm] = useState<Required<BrandOverridePatch>>({
		displayName: "",
		description: "",
		hidden: false,
		mergeInto: "",
		note: "",
	});

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const data = await fetchJson<BrandDetail>(
				`/brands/${encodeURIComponent(slug)}/detail`,
			);
			setBrand(data);
			setForm({
				// `name` already carries the override where one exists; `rollupName` is what the feeds
				// say. Only show a display name in the field when it actually differs.
				displayName: data.name === data.rollupName ? "" : data.name,
				description: data.description ?? "",
				hidden: data.hidden,
				mergeInto: data.mergedInto ?? "",
				note: data.note ?? "",
			});
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setLoading(false);
		}
	}, [slug]);

	useEffect(() => {
		load();
	}, [load]);

	const save = async () => {
		setSaving(true);
		setSaveError(null);
		setSaved(false);
		try {
			const updated = await sendJson<BrandDetail>(
				`/brands/${encodeURIComponent(slug)}`,
				"PATCH",
				{
					displayName: form.displayName?.trim() || null,
					description: form.description?.trim() || null,
					hidden: form.hidden,
					mergeInto: form.mergeInto?.trim() || null,
					note: form.note?.trim() || null,
				},
			);
			setBrand(updated);
			setSaved(true);
		} catch (err) {
			// The server answers 400 with the reason a merge was refused (unknown target, a chain,
			// self-reference). Showing it beats a bare status code.
			setSaveError(err instanceof Error ? err.message : String(err));
		} finally {
			setSaving(false);
		}
	};

	const clearOverride = async () => {
		setSaving(true);
		setSaveError(null);
		try {
			await sendJson(`/brands/${encodeURIComponent(slug)}/override`, "DELETE");
			await load();
		} catch (err) {
			setSaveError(err instanceof Error ? err.message : String(err));
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center py-20">
				<div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
			</div>
		);
	}

	if (error || !brand) {
		return (
			<div className="space-y-4">
				<Link
					to="/brands"
					className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-primary"
				>
					<ArrowLeft className="w-4 h-4" /> Brands
				</Link>
				<div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
					{error ?? "Brand not found"}
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<Link
						to="/brands"
						className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-primary"
					>
						<ArrowLeft className="w-4 h-4" /> Brands
					</Link>
					<h1 className="mt-2 text-2xl font-bold text-slate-800">
						{brand.name}
					</h1>
					<p className="font-mono text-xs text-slate-400">{brand.slug}</p>
				</div>
				<a
					href={brandPageUrl(brand.slug)}
					target="_blank"
					rel="noopener noreferrer"
					className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 hover:border-primary/40 hover:text-primary transition-colors"
				>
					<ExternalLink className="w-4 h-4" /> Open brand page
				</a>
			</div>

			<div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
				<div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
					<p className="text-xs font-semibold text-slate-400 uppercase">
						Products
					</p>
					<p className="mt-1 text-xl font-bold text-slate-800">
						{brand.productCount.toLocaleString()}
					</p>
				</div>
				<div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
					<p className="text-xs font-semibold text-slate-400 uppercase">
						Comparable
					</p>
					<p className="mt-1 text-xl font-bold text-slate-800">
						{brand.comparableCount.toLocaleString()}
					</p>
					<p className="text-[11px] text-slate-400">
						gate is {INDEXABLE_COMPARABLE}
					</p>
				</div>
				<div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
					<p className="text-xs font-semibold text-slate-400 uppercase">
						Robots
					</p>
					<p
						className={`mt-1 text-xl font-bold ${brand.indexable ? "text-emerald-600" : "text-slate-500"}`}
					>
						{brand.indexable ? "index" : "noindex"}
					</p>
				</div>
				<div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
					<p className="text-xs font-semibold text-slate-400 uppercase">
						Root category
					</p>
					<p className="mt-1 text-sm font-medium text-slate-700">
						{brand.rootCategoryName ?? "–"}
					</p>
				</div>
			</div>

			{brand.suspectMerchantName && (
				<div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
					<Store className="mt-0.5 w-4 h-4 shrink-0" />
					<p>
						This brand name matches a validated merchant's name — a shop that
						puts its own name in its feed's{" "}
						<code className="text-xs">brand</code> field. Check the merchant
						list below before deciding: some shops really are brands. Hide it if
						it is not one.
					</p>
				</div>
			)}

			<div className="grid gap-6 lg:grid-cols-3">
				<div className="lg:col-span-2 space-y-6">
					<Section
						title="Aliases"
						subtitle="Every raw feed spelling behind this slug. The brand page filters the search index on all of them."
					>
						{brand.aliases.length === 0 ? (
							<p className="text-sm text-slate-400">No aliases stored.</p>
						) : (
							<ul className="flex flex-wrap gap-2">
								{brand.aliases.map((alias, i) => (
									<li
										key={alias.value}
										className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${
											i === 0
												? "border-primary/40 bg-primary/5 text-slate-700"
												: "border-slate-200 bg-white text-slate-600"
										}`}
										title={
											i === 0
												? "Strongest spelling — supplies the display name"
												: undefined
										}
									>
										<span className="font-medium">{alias.value}</span>
										<span className="text-slate-400">
											{alias.productCount.toLocaleString()}
										</span>
									</li>
								))}
							</ul>
						)}
					</Section>

					<Section title="Top categories">
						{brand.categories.length === 0 ? (
							<p className="text-sm text-slate-400">No categorised products.</p>
						) : (
							<ul className="space-y-1.5">
								{brand.categories.map((c) => (
									<li
										key={c.categoryId}
										className="flex items-center justify-between text-sm"
									>
										<span className="text-slate-600">{c.name}</span>
										<span className="text-xs text-slate-400">
											{c.productCount.toLocaleString()}
										</span>
									</li>
								))}
							</ul>
						)}
					</Section>

					<Section
						title="Merchants supplying this brand"
						subtitle="A brand carried by exactly one shop, named after that shop, is a shop name."
					>
						{brand.merchants.length === 0 ? (
							<p className="text-sm text-slate-400">No offers.</p>
						) : (
							<ul className="space-y-1.5">
								{brand.merchants.map((m) => (
									<li
										key={m.id}
										className="flex items-center justify-between text-sm"
									>
										<Link
											to={`/merchants/${m.id}`}
											className="text-slate-600 hover:text-primary"
										>
											{m.name}
										</Link>
										<span className="text-xs text-slate-400">
											{m.productCount.toLocaleString()}
										</span>
									</li>
								))}
							</ul>
						)}
					</Section>

					<Section title="Sample products">
						{brand.sampleProducts.length === 0 ? (
							<p className="text-sm text-slate-400">No products.</p>
						) : (
							<ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
								{brand.sampleProducts.map((p) => (
									<li key={p.id}>
										<Link
											to={`/products/${p.id}`}
											className="block rounded-lg border border-slate-200 p-2 hover:border-primary/40 transition-colors"
										>
											{p.image ? (
												<img
													src={p.image}
													alt={p.name}
													className="h-20 w-full rounded object-contain"
												/>
											) : (
												<div className="flex h-20 items-center justify-center rounded bg-slate-50">
													<Package className="w-5 h-5 text-slate-300" />
												</div>
											)}
											<p className="mt-2 line-clamp-2 text-[11px] text-slate-600">
												{p.name}
											</p>
											<p className="text-[11px] text-slate-400">
												{p.offerCount} offers
											</p>
										</Link>
									</li>
								))}
							</ul>
						)}
					</Section>
				</div>

				<div className="space-y-6">
					<Section
						title="Curation"
						subtitle="Stored in brand_overrides, keyed by slug. Takes effect immediately; the nightly rebuild folds it into the rollup."
					>
						<div className="space-y-4">
							<label className="block">
								<span className="text-xs font-semibold text-slate-500">
									Display name
								</span>
								<input
									type="text"
									value={form.displayName ?? ""}
									placeholder={brand.rollupName}
									onChange={(e) =>
										setForm((f) => ({ ...f, displayName: e.target.value }))
									}
									className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary/50"
								/>
							</label>

							<label className="block">
								<span className="text-xs font-semibold text-slate-500">
									Description
								</span>
								<textarea
									rows={4}
									value={form.description ?? ""}
									placeholder="Rendered above the product listing on the brand page."
									onChange={(e) =>
										setForm((f) => ({ ...f, description: e.target.value }))
									}
									className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary/50"
								/>
							</label>

							<label className="flex items-start gap-2">
								<input
									type="checkbox"
									checked={form.hidden}
									onChange={(e) =>
										setForm((f) => ({ ...f, hidden: e.target.checked }))
									}
									className="mt-0.5"
								/>
								<span className="text-sm text-slate-600">
									<span className="font-semibold">Hidden</span>
									<span className="block text-xs text-slate-400">
										Dropped from the hub and the sitemap. The page still
										answers, as noindex.
									</span>
								</span>
							</label>

							<label className="block">
								<span className="text-xs font-semibold text-slate-500">
									Merge into (slug)
								</span>
								<input
									type="text"
									value={form.mergeInto ?? ""}
									placeholder="e.g. fjallraven"
									onChange={(e) =>
										setForm((f) => ({ ...f, mergeInto: e.target.value }))
									}
									className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm outline-none focus:border-primary/50"
								/>
								<span className="mt-1 block text-xs text-slate-400">
									This brand's products fold into the target and its URL 301s
									there. One hop only — the target may not itself be merged.
								</span>
							</label>

							<label className="block">
								<span className="text-xs font-semibold text-slate-500">
									Note
								</span>
								<input
									type="text"
									value={form.note ?? ""}
									placeholder="Why this decision was made"
									onChange={(e) =>
										setForm((f) => ({ ...f, note: e.target.value }))
									}
									className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary/50"
								/>
							</label>

							{saveError && (
								<p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
									{saveError}
								</p>
							)}
							{saved && !saveError && (
								<p className="text-xs font-medium text-emerald-600">Saved.</p>
							)}

							<div className="flex items-center gap-2">
								<button
									type="button"
									onClick={save}
									disabled={saving}
									className="inline-flex items-center gap-1.5 rounded-full bg-brand hover:bg-brand-hover px-3 py-2 text-sm font-semibold text-black disabled:opacity-50"
								>
									<Save className="w-4 h-4" /> Save
								</button>
								{brand.hasOverride && (
									<button
										type="button"
										onClick={clearOverride}
										disabled={saving}
										className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:border-red-300 hover:text-red-600 disabled:opacity-50"
									>
										<Trash2 className="w-4 h-4" /> Clear
									</button>
								)}
							</div>

							{brand.hasOverride && brand.updatedBy && (
								<p className="text-xs text-slate-400">
									Last edited by {brand.updatedBy}
									{brand.overrideUpdatedAt
										? ` · ${new Date(brand.overrideUpdatedAt).toLocaleString()}`
										: ""}
								</p>
							)}
						</div>
					</Section>

					<Section
						title="Search Console"
						subtitle={`The /maerke/ page group, last ${brand.searchConsole.days} days. Zeros mean Google has not served this page yet.`}
					>
						<ul className="space-y-1.5 text-sm">
							<li className="flex items-center justify-between">
								<span className="text-slate-500">Impressions</span>
								<span className="text-slate-700">
									{brand.searchConsole.impressions.toLocaleString()}
								</span>
							</li>
							<li className="flex items-center justify-between">
								<span className="text-slate-500">Clicks</span>
								<span className="text-slate-700">
									{brand.searchConsole.clicks.toLocaleString()}
								</span>
							</li>
							<li className="flex items-center justify-between">
								<span className="text-slate-500">Avg. position</span>
								<span className="text-slate-700">
									{brand.searchConsole.avgPosition === null
										? "–"
										: brand.searchConsole.avgPosition.toFixed(1)}
								</span>
							</li>
						</ul>
					</Section>

					<Section
						title="Similar slugs"
						subtitle="Same normalised key — almost certainly the same brand spelled differently."
					>
						{brand.similar.length === 0 ? (
							<p className="text-sm text-slate-400">None.</p>
						) : (
							<ul className="space-y-2">
								{brand.similar.map((s) => (
									<li
										key={s.slug}
										className="flex items-center justify-between gap-2 text-sm"
									>
										<Link
											to={`/brands/${encodeURIComponent(s.slug)}`}
											className="min-w-0 flex-1 truncate text-slate-600 hover:text-primary"
										>
											{s.name}
											<span className="ml-1 font-mono text-[11px] text-slate-400">
												{s.slug}
											</span>
										</Link>
										<button
											type="button"
											onClick={() =>
												setForm((f) => ({ ...f, mergeInto: s.slug }))
											}
											className="inline-flex shrink-0 items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-[11px] text-slate-500 hover:border-primary/40 hover:text-primary"
										>
											<GitMerge className="w-3 h-3" /> Merge into
										</button>
									</li>
								))}
							</ul>
						)}
					</Section>

					<Section title="State">
						<ul className="space-y-1.5 text-sm">
							<li className="flex items-center justify-between">
								<span className="text-slate-500">Visibility</span>
								<span className="inline-flex items-center gap-1 text-slate-700">
									{brand.hidden ? (
										<>
											<EyeOff className="w-3.5 h-3.5" /> Hidden
										</>
									) : (
										<>
											<Eye className="w-3.5 h-3.5" /> Listed
										</>
									)}
								</span>
							</li>
							<li className="flex items-center justify-between">
								<span className="text-slate-500">Merged into</span>
								<span className="font-mono text-xs text-slate-700">
									{brand.mergedInto ?? "–"}
								</span>
							</li>
							<li className="flex items-center justify-between">
								<span className="text-slate-500">Normalised key</span>
								<span className="font-mono text-xs text-slate-700">
									{brand.normKey || "–"}
								</span>
							</li>
							<li className="flex items-center justify-between">
								<span className="text-slate-500">Rollup row written</span>
								<span className="text-xs text-slate-700">
									{brand.updatedAt
										? new Date(brand.updatedAt).toLocaleString()
										: "–"}
								</span>
							</li>
						</ul>
					</Section>
				</div>
			</div>
		</div>
	);
};

export default Brand;
